import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { matchPlayerApi } from '../../services/footballApi';

interface MatchPlayer {
  id: number;
  match?: { name: string } | null;
  player?: { name: string } | null;
  team?: { name: string } | null;
  position?: string;
  lineup_status?: string;
  credits?: number;
  total_points?: number;
  created_at?: string;
  updated_at?: string;
}

const FootballMatchPlayers: React.FC = () => {
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchMatchPlayers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await matchPlayerApi.getAll();
      setMatchPlayers(res.data);
    } catch (err: any) {
      setError('Failed to fetch match players');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatchPlayers();
  }, [fetchMatchPlayers]);

  const handleEdit = (id: number) => {
    navigate(`/football/match-players/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this match player?')) return;
    try {
      await matchPlayerApi.delete(id);
      fetchMatchPlayers();
    } catch (err) {
      alert('Failed to delete match player');
    }
  };

  const handleCreate = () => {
    navigate('/football/match-players/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'match', headerName: 'Match', flex: 1, minWidth: 160, valueGetter: (value: MatchPlayer['match']) => value?.name || '' },
    { field: 'player', headerName: 'Player', flex: 1, minWidth: 120, valueGetter: (value: MatchPlayer['player']) => value?.name || '' },
    { field: 'team', headerName: 'Team', flex: 1, minWidth: 120, valueGetter: (value: MatchPlayer['team']) => value?.name || '' },
    { field: 'position', headerName: 'Position', width: 100 },
    { field: 'lineup_status', headerName: 'Lineup Status', width: 120 },
    { field: 'credits', headerName: 'Credits', width: 100, type: 'number' },
    { field: 'total_points', headerName: 'Points', width: 100, type: 'number' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: MatchPlayer }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Match Players</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Match Player
        </Button>
      </Stack>
      <DataGrid
        rows={matchPlayers}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballMatchPlayers; 